// src/components/Admin/BoardCategoryManager/CategoryList.jsx
import React, { useState } from 'react';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Snackbar, Alert, Chip, Accordion, AccordionSummary,
  AccordionDetails, Typography, Box, Tooltip, useMediaQuery,
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward, Edit, Delete,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { moveCategory, deleteCategory, editCategory } from '../../../api/admin';
import { useCategory } from '../../../contexts/CategoryContext';

export default function CategoryList({ categories = [], onEdit, reload }) {
  const { reloadCategories } = useCategory();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const notify = (msg) => { setSnackbarMsg(msg); setSnackbarOpen(true); };

  /* ───────── 공통 핸들러 ───────── */
  const handleMove = (id, dir) => {
    moveCategory(id, dir)
      .then(() => { reload(); reloadCategories(); notify('순서가 바뀌었습니다!'); })
      .catch(() => notify('순서 변경 실패'));
  };
  const handleDelete = (id) => {
    if (!window.confirm('정말 삭제할까요?')) return;
    deleteCategory(id)
      .then(() => { reload(); reloadCategories(); notify('카테고리가 삭제되었습니다.'); })
      .catch(() => notify('삭제 실패'));
  };
  const handleToggleActive = (cat) => {
    editCategory(cat.id, { is_active: !cat.is_active })
      .then(() => { reload(); reloadCategories(); notify(cat.is_active ? '숨김 처리되었습니다.' : '노출 처리되었습니다.'); })
      .catch(() => notify('상태 변경 실패'));
  };

  /* ──────────────────────────────────────────────
     모바일 뷰 (아코디언)
  ────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {categories.map((cat) => (
            <Accordion key={cat.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, pr: 2 }}>
                  <Typography fontWeight="bold">
                    {cat.emoji} {cat.name}
                  </Typography>

                  {/* 그룹 Chip (새로 추가) */}
                  {cat.group?.id && (
                    <Chip
                      label={`${cat.group.emoji} ${cat.group.name}`}
                      size="small"
                      color="primary"
                      sx={{ alignSelf: 'flex-start' }}
                    />
                  )}

                  <Chip
                    label={cat.is_active ? '노출중' : '숨김'}
                    size="small"
                    color={cat.is_active ? 'success' : 'default'}
                    icon={cat.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(cat); }}
                    clickable
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1 }}>{cat.description}</Typography>

                {/* 속성 Chip 모음 */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <Chip label={`익명: ${cat.anonymous_option === 'force' ? 'O' : 'X'}`} size="small" />
                  <Chip label={`비밀글: ${cat.secret_option === 'force' ? 'O' : 'X'}`} size="small" />
                  <Chip label={`읽기비용: ${cat.read_cost}🌿`} size="small" />
                  <Chip
                    label={`작성 권한: ${cat.write_permission === 'all' ? '전체' :
                      cat.write_permission === 'admin' ? '어드민 이상' : '마스터만'
                      }`}
                    size="small"
                  />
                </Box>

                {/* 이동 버튼 */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
                  <Tooltip title="위로 이동">
                    <IconButton size="small" onClick={() => handleMove(cat.id, 'up')}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="아래로 이동">
                    <IconButton size="small" onClick={() => handleMove(cat.id, 'down')}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* 관리 버튼 */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Tooltip title="수정">
                    <IconButton size="small" onClick={() => onEdit(cat)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="삭제">
                    <IconButton size="small" onClick={() => handleDelete(cat.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </>
    );
  }

  /* ──────────────────────────────────────────────
     데스크톱 뷰 (테이블)
  ────────────────────────────────────────────── */
  return (
    <>
      <TableContainer component={Paper}
        sx={{
          mt: 2,
          '& .MuiTableCell-root': { py: 1, px: 2 },
          '& .MuiTableHead-root': { bgcolor: 'grey.100' },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" width={80}>순서</TableCell>
              <TableCell align="center" width={80}>이모지</TableCell>

              {/* ★ 그룹 컬럼 */}
              <TableCell align="center" width={120}>그룹</TableCell>

              <TableCell>게시판명</TableCell>
              <TableCell>설명</TableCell>
              <TableCell align="center">익명글</TableCell>
              <TableCell align="center">비밀글</TableCell>
              <TableCell align="center">작성 권한</TableCell>
              <TableCell align="center">읽기 비용</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="center" width={120}>관리</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {categories.map((cat) => (
              <TableRow key={cat.id} hover>
                {/* 순서 이동 */}
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleMove(cat.id, 'up')}>
                    <ArrowUpward fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleMove(cat.id, 'down')}>
                    <ArrowDownward fontSize="inherit" />
                  </IconButton>
                </TableCell>

                <TableCell align="center">{cat.emoji}</TableCell>

                {/* 그룹 표시 */}
                <TableCell align="center">
                  {cat.group?.id ? `${cat.group.emoji} ${cat.group.name}` : '-'}
                </TableCell>

                <TableCell>{cat.name}</TableCell>
                <TableCell>{cat.description}</TableCell>

                <TableCell align="center">
                  {cat.anonymous_option === 'force' ? '익명' : '공개'}
                </TableCell>
                <TableCell align="center">
                  {cat.secret_option === 'force' ? '비밀글' : '공개글'}
                </TableCell>

                <TableCell align="center">
                  {cat.write_permission === 'all'
                    ? '전체'
                    : cat.write_permission === 'admin'
                      ? '어드민 이상'
                      : '마스터만'}
                </TableCell>

                <TableCell align="center">{cat.read_cost}🌿</TableCell>

                <TableCell align="center">
                  <Chip
                    label={cat.is_active ? '노출중' : '숨김'}
                    size="small"
                    color={cat.is_active ? 'success' : 'default'}
                    icon={cat.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    onClick={() => handleToggleActive(cat)}
                    clickable
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton size="small" onClick={() => onEdit(cat)}>
                    <Edit fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(cat.id)}>
                    <Delete fontSize="inherit" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
